import React, { useState, useEffect, useRef, useMemo } from 'react';
import Toolbar from '@material-ui/core/Toolbar';
import Card from '@material-ui/core/Card';
import { ITransformer } from '../../../@types/model/transformer/transformer';
import MaterialTable from '../../customComponents/materialTable/Table';
import TransformerDropdown from '../../customComponents/selector/transformer/TransformerSelector';
import TransformerPrepaidReadingHelper, { ITransformerPrepaidReading } from '../../../@types/model/transformer/reading/transformerPrepaidReading';
import generalFunctions from '../../../store/general/functions';
import lodash from 'lodash';
import appFunctionsService from '../../../services/appFunctionsService';
import Tooltip from '@material-ui/core/Tooltip';
import { IRootState } from '../../../@types/redux';
import { connect } from 'react-redux';
import { IUserSession } from '../../../@types/employee';
import { Warning } from '@material-ui/icons';
import moment from 'moment';
import { Fab, Icon } from '@material-ui/core';
import { CSVLink } from 'react-csv';
import DateRangePicker from '../../customComponents/input/DateRangePicker';
import EditTransformerPrepaidReadingDialog from './dialog/invoice/prepaid/PrepaidMeterReadingEditDialog';
import CarouselImageViewer from '../../customComponents/image/CarouselViewer';
import TransformerPrepaidReadingDeleteDialog from './dialog/invoice/prepaid/PrepaidMeterReadingDeleteDialog';
import EmbedFileDialog from '../../customComponents/dialog/EmbededFileDialog';
import Screen from '../../customComponentV2/screen/Screen';

interface ITransformerPrepaidMeterListProps {
    isTransformerAdmin : boolean;

    session ?: IUserSession | null;
}

const TransformerPrepaidMeterListComponent = (props : ITransformerPrepaidMeterListProps) : React.ReactElement => {
    const [transformerNumber, setTransformerNumber] = useState<string | undefined>(undefined);
    const [readings, setReadings] = useState<Array<ITransformerPrepaidReading>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dateFrom, setDateFrom] = useState<moment.Moment | null>(null);
    const [dateTo, setDateTo] = useState<moment.Moment | null>(null);
    const [onlyErrors, setOnlyErrors] = useState<boolean>(false);
    const [isCarouselOpen, setIsCarouselOpen] = useState<boolean>(false);
    const [imageIndex, setImageIndex] = useState<number>(0);
    const [embedSrc, setEmbedSrc] = useState<string | undefined>(undefined);

    const csvLink = useRef<CSVLink & HTMLAnchorElement>(null);
    const readingListener = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
        loadData();
        return () => {
            if (readingListener.current) {
                readingListener.current();
            }
        };
    }, []);

    const loadData = async () : Promise<void> => {
        if (readingListener.current) readingListener.current();

        setIsLoading(true);

        const session = props.session;

        if (!session) return;

        readingListener.current = TransformerPrepaidReadingHelper.listen().onSnapshot((snap) => {
            setReadings(readings => {
                const currentReadings = readings.slice();
    
                snap.docChanges().forEach((f) => {
                    const item = f.doc.data();
                    if (
                        !session.employee.IsTransformerAdmin &&
                        item.division &&
                        session.employee.Areas.length &&
                        (!session.employee.Areas.includes(item.division) && !session.employee.Areas.includes(item.subArea))) return;
    
                    const index = lodash.findIndex(currentReadings, n => n.id === item.id);
    
                    switch (f.type) {
                        case 'added':
                            currentReadings.push(item);
                            break;
                        case 'modified':
                            currentReadings.splice(index, 1, item);
                            break;
                        case 'removed':
                            currentReadings.splice(index, 1);
                            break;
                    }
                });
    
                return currentReadings;
            });

            setIsLoading(false);
        }, (err) => {
            generalFunctions.generalShowErrorSnackbar(err.message);
        });
    };
    
    const filteredReadings = useMemo<Array<ITransformerPrepaidReading>>(() => {
        return readings
            .filter(n => !transformerNumber || n.transformerRef === transformerNumber)
            .filter(n => !dateFrom || n.date >= dateFrom.valueOf())
            .filter(n => !dateTo || n.date <= dateTo.valueOf())
            .filter(n => !onlyErrors || hasError(n))
            .sort((a, b) => b.date - a.date);
    }, [readings, transformerNumber, dateFrom, dateTo, onlyErrors]);

    const images = useMemo<Array<string>>(() => {
        const imageSrcs = readings
            .filter(x => !x.imageFileName?.includes('.pdf'))
            .filter(x => !!x.imageFileUrl)
            .map(n => n.imageFileUrl ?? '');

        return imageSrcs;
    }, [readings]);

    const onEPNumberChange = (value ?: ITransformer) : void => {
        setTransformerNumber(value?.EPNumber);
    };

    const hasError = (reading : ITransformerPrepaidReading) : boolean => reading.amount < 0;

    const onDelete = async (reading : ITransformerPrepaidReading) : Promise<void> => {
        const session = props.session;

        if (!session?.employee.IsTransformerAdmin) return;

        await TransformerPrepaidReadingHelper.delete(reading.id);
    };
    
    const onDateSelectionChange = (start : moment.Moment | null, end : moment.Moment | null) : void => {
        setDateFrom(!start ? null : moment(start));
        setDateTo(!end ? null : moment(end));
    };

    const onShowOnlyErrorClick = () : void => {
        setOnlyErrors(!onlyErrors);
    };

    const onCSVClick = () : void => {
        if (csvLink.current) {
            (csvLink.current as any).link.click();
        }
    };

    const csvData = useMemo<Array<any>>(() => {
        return filteredReadings.map(n => ({
            transformer: n.transformerRef,
            date: appFunctionsService.formatDateTime(n.date),
            division: n.division,
            group: n.group,
            amount: n.amount,
            createdByName: n.createdByName,
            createdByEmployee: n.createdByEmployee,
            createdOn: appFunctionsService.formatDateTime(n.createdOn),
            updatedByName: n.updatedByName,
            updatedByEmployee: n.updatedByEmployee,
            updatedOn: appFunctionsService.formatDateTime(n.updatedOn),
        }));
    }, [filteredReadings]);

    const onImageClick = (event : React.MouseEvent<HTMLImageElement>) : void => {
        const imageSrcs = images;

        const index = imageSrcs.indexOf(event.currentTarget.id);

        if (index >= 0) {
            setIsCarouselOpen(true);
            setImageIndex(index);
        } else {
            setEmbedSrc(event.currentTarget.id);
        }
    };

    const onCarouselClose = () : void => {
        setIsCarouselOpen(false);
        setImageIndex(0);
    };

    const onEmbedClose = () : void => {
        setEmbedSrc(undefined);
    };

    const csvName = useMemo<number>(() => moment().valueOf(), []);

    return (
        <Screen
            useAnimatedLoader    
            isPadded={false}
            isScrollable={false}>
            <div className={'fdc flx1 p10 mh0 mw0 bcg0'}>
                <Toolbar>
                    <span className={'flx1'} />
                    <div style={{ paddingRight: '15px' }}>
                    </div>
                    <div className='pr20'>
                        <Tooltip title='Show only Errors'>
                            <Fab color={onlyErrors ? 'primary' : 'default'} size='small' onClick={onShowOnlyErrorClick}>
                                <Icon className='fs20'>
                                    warning
                                </Icon>
                            </Fab>
                        </Tooltip>
                    </div>
                    <div className={'aic p5 mb10 w350'}>
                        <DateRangePicker onChange={onDateSelectionChange} start={dateFrom} end={dateTo} />
                    </div>
                    <div className={'fdr pt5 jcfe aic'}>
                        <div className='fdc flx1 w240'>
                            <TransformerDropdown
                                type='prepaid'
                                value={transformerNumber}
                                label='Select Transformer'
                                fullWidth
                                onChange={onEPNumberChange}
                            />
                        </div>
                    </div>
                </Toolbar>
                <Card className={'flx1 fdc mb70'}>
                    <MaterialTable<ITransformerPrepaidReading>
                        id='transformerReadingsTable'
                        data={filteredReadings}
                        isLoading={isLoading}
                        rowsPerPage={50}
                        externalSort
                        onExportCsvClick={onCSVClick}
                        columns={[{
                            header: '',
                            field: 'transformerRef',
                            width: 130,
                            renderCell: row => (
                                <div className=' fdr aic'>
                                    {
                                        (props.isTransformerAdmin || (row.createdBy === props.session?.firebaseUser.uid)) &&
                                        <EditTransformerPrepaidReadingDialog
                                            key={`${row.guid}_edit`}
                                            reading={row}
                                            session={props.session}
                                        />
                                    }
                                    {
                                        (props.isTransformerAdmin || (row.createdBy === props.session?.firebaseUser.uid)) &&
                                        <TransformerPrepaidReadingDeleteDialog
                                            key={`${row.guid}_delete`}
                                            reading={row}
                                            onDelete={onDelete}
                                        />

                                    }
                                    {
                                        !!row.isWeb &&
                                        <Icon className='cp ml10'>
                                            laptop
                                        </Icon>                                            
                                    }
                                    {
                                        !row.isWeb &&
                                        <Icon className='cp ml10'>
                                            smartphone
                                        </Icon>                                            
                                    }
                                    {
                                        hasError(row) &&
                                        <Tooltip title={'Transformer reading has a negative usage'}>
                                            <Warning className={'cy pl10'}/>
                                        </Tooltip>
                                    }
                                </div>
                            ),
                        }, {
                            header: 'Date',
                            field: 'date',
                            width: 125,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (<span>{appFunctionsService.formatDateTimeToDateOnly(row.date)}</span>),
                        }, {
                            header: 'EP Point',
                            field: 'transformerRef',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Area',
                            field: 'division',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Sub Area',
                            field: 'subArea',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Group',
                            field: 'group',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                        }, {
                            header: 'Electricity VAT',
                            field: 'electricityVat',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {(!row.electricityVat ? 0 : row.electricityVat).toLocaleCurrency()}
                                </div>
                            ),
                        }, {
                            header: 'Electricity Amount',
                            field: 'electricityExlVat',
                            width: 210,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {(!row.electricityExlVat ? 0 : row.electricityExlVat).toLocaleCurrency()}
                                </div>
                            ),
                        }, {
                            header: 'Electricity Units',
                            field: 'electricityUnits',
                            width: 210,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {(!row.electricityUnits ? 0 : row.electricityUnits)} kWh
                                </div>
                            ),
                        }, {
                            header: 'Water VAT',
                            field: 'waterVat',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {(!row.waterVat ? 0 : row.waterVat).toLocaleCurrency()}
                                </div>
                            ),
                        }, {
                            header: 'Water Amount',
                            field: 'waterExlVat',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {(!row.waterExlVat ? 0 : row.waterExlVat).toLocaleCurrency()}
                                </div>
                            ),
                        }, {
                            header: 'VAT',
                            field: 'vatPercentage',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {!row.vatPercentage ? 'N/A' : `${row.vatPercentage} %`}
                                </div>
                            ),
                        }, {
                            header: 'VAT Subtotal',
                            field: 'vatTotal',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {(!row.vatTotal ? 0 : row.vatTotal).toLocaleCurrency()}
                                </div>
                            ),
                        }, {
                            header: 'Total Amount',
                            field: 'amount',
                            width: 200,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <div className=''>
                                    {(!row.amount ? 0 : row.amount).toLocaleCurrency()}
                                </div>
                            ),
                        }, {
                            header: 'File',
                            field: 'imageFileUrl',
                            width: 100,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (row.imageFileUrl ?
                                <Icon id={row.imageFileUrl} className={'cpd curp aic'} onClick={onImageClick}>
                                    check_circle
                                </Icon> :
                                <Icon className={'cr aic'}>
                                    close
                                </Icon>
                            ),
                        }, {
                            header: 'Created By',
                            field: 'createdByName',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <Tooltip title={row.createdByEmployee}>
                                    <div>
                                        {
                                            row.createdByName
                                        }
                                    </div>
                                </Tooltip>
                            ),
                        }, {
                            header: 'Updated By',
                            field: 'updatedByName',
                            width: 145,
                            enableSort: true,
                            enableFilter: true,
                            renderCell: row => (
                                <Tooltip title={row.updatedByEmployee}>
                                    <div>
                                        {
                                            row.updatedByName
                                        }
                                    </div>
                                </Tooltip>
                            ),
                        }]}
                    />
                </Card>
                <div className='fdr jcfe'>
                    <EditTransformerPrepaidReadingDialog
                        aria-label='add'
                        session={props.session}
                    />
                </div>
                <CSVLink
                    data={csvData}
                    headers={[
                        {
                            key: 'transformer',
                            label: 'EP Number',
                        },
                        {
                            key: 'date',
                            label: 'Date',
                        },
                        {
                            key: 'division',
                            label: 'Area',
                        },
                        {
                            key: 'Group',
                            label: 'group',
                        },
                        {
                            key: 'amount',
                            label: 'Amount',
                        },
                        {
                            key: 'createdByName',
                            label: 'CreatedByName',
                        },
                        {
                            key: 'createdByEmployee',
                            label: 'CreatedByEmployee',
                        },
                        {
                            key: 'createdOn',
                            label: 'CreatedOn',
                        },
                        {
                            key: 'updatedByName',
                            label: 'UpdatedByName',
                        },
                        {
                            key: 'updatedByEmployee',
                            label: 'UpdatedByEmployee',
                        },
                        {
                            key: 'updatedOn',
                            label: 'UpdatedOn',
                        },
                    ]}
                    filename={`${csvName}_prepaid_transformers.csv`}
                    className='dn'
                    ref={csvLink}
                    target='_blank'/>
                {
                    images.length > 0 &&
                    <CarouselImageViewer index={imageIndex} isOpen={isCarouselOpen} imageSrcs={images} onClose={onCarouselClose} />
                }
                <EmbedFileDialog src={embedSrc} onClose={onEmbedClose} fullWidth maxWidth='md' />
            </div>
        </Screen>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        isTransformerAdmin: !!state.auth.session?.employee.IsTransformerAdmin,
        session: state.auth.session,
    };
};

const TransformerPrepaidMeterList = connect(
    mapStateToProps,
)(TransformerPrepaidMeterListComponent);

export default TransformerPrepaidMeterList;
